import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Loader2, Plus, Mail, Edit, Eye, Trash2, Search, Paperclip, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import RichTextEditor from '../ui/RichTextEditor';
import TemplateVariablesCard from '../shared/TemplateVariablesCard';

const EMAIL_TEMPLATE_TYPES = [
  { value: 'buyer_thank_you',                   label: 'Buyer Thank You' },
  { value: 'deliverables_nudge',                label: 'Deliverables Nudge' },
  { value: 'document_for_signature_horse',      label: 'Document for Signature - Horse' },
  { value: 'document_for_signature_sale',       label: 'Document for Signature - Sale' },
  { value: 'event_reminder',                    label: 'Event Reminder' },
  { value: 'hip_numbers',                       label: 'Hip Numbers' },
  { value: 'new_alert',                         label: 'New Alert Notification' },
  { value: 'new_invoice',                       label: 'New Invoice' },
  { value: 'new_invoice_statement',             label: 'New Invoice Statement' },
  { value: 'new_report',                        label: 'New Report' },
  { value: 'new_report_dsu',                    label: 'New Report - Daily Sale Update' },
  { value: 'new_tenant_signup',                 label: 'New Tenant Signup Alert (System Owner)' },
  { value: 'password_reset_confirmation',       label: 'Password Reset Confirmation' },
  { value: 'password_reset_link',               label: 'Password Reset Link' },
  { value: 'payment_confirmation',              label: 'Payment Confirmation' },
  { value: 'seller_thank_you',                  label: 'Seller Thank You' },
  { value: 'ship_in_note',                      label: 'Ship In Note' },
  { value: 'task_assignment',                   label: 'Task Assignment' },
  { value: 'task_reminder',                     label: 'Task Reminder' },
  { value: 'welcome',                           label: 'Welcome Email' },
  { value: 'welcome_owner',                     label: 'Welcome Email - Owner' },
  { value: 'welcome_new_tenant',                label: 'Welcome - New Tenant' },
];

const EmailTemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [customVariables, setCustomVariables] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [newVarKey, setNewVarKey] = useState('');
  const [newVarLabel, setNewVarLabel] = useState('');
  const [newVarFieldPath, setNewVarFieldPath] = useState('');
  const [newVarStaticValue, setNewVarStaticValue] = useState('');
  const [newVarMode, setNewVarMode] = useState('field'); // 'field' | 'static'
  const [catalogSearch, setCatalogSearch] = useState('');
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const attachmentInputRef = useRef(null);
  const [formData, setFormData] = useState({
    template_type: '',
    subject: '',
    body: '',
    is_active: true,
    attachment_file_id: null,
    attachment_filename: null,
  });

  useEffect(() => {
    fetchTemplates();
    fetchCustomVariables();
    api.get('/system-settings/template-variables/catalog').then(r => setCatalog(r.data || {})).catch(() => {});
  }, []);

  const fetchCustomVariables = async () => {
    try {
      const res = await api.get('/system-settings/template-variables');
      setCustomVariables(res.data || []);
    } catch { /* ignore */ }
  };

  const addCustomVariable = async () => {
    if (!newVarKey.trim() || !newVarLabel.trim()) return;
    const key = newVarKey.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    const payload = {
      key,
      label: newVarLabel.trim(),
      field_path: newVarMode === 'field' ? newVarFieldPath : '',
      static_value: newVarMode === 'static' ? newVarStaticValue.trim() : '',
    };
    try {
      await api.post('/system-settings/template-variables', payload);
      setNewVarKey(''); setNewVarLabel(''); setNewVarFieldPath(''); setNewVarStaticValue('');
      fetchCustomVariables();
      toast.success('Variable added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add'); }
  };

  const removeCustomVariable = async (key) => {
    try {
      await api.delete(`/system-settings/template-variables/${key}`);
      fetchCustomVariables();
      toast.success('Variable removed');
    } catch { toast.error('Failed to remove'); }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/email-templates');
      setTemplates((response.data || []).sort((a, b) => (a.name || a.template_type || '').localeCompare(b.name || b.template_type || '')));
    } catch (error) {
      console.error('Error fetching templates:', error);
      // Template endpoint might not exist yet
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingTemplate) {
        await api.put(`/email-templates/${editingTemplate.id}`, formData);
        toast.success('Template updated');
      } else {
        await api.post('/email-templates', formData);
        toast.success('Template created');
      }
      setDialogOpen(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setConfirmingDelete(false);
    setFormData({
      template_type: template.template_type,
      subject: template.subject,
      body: template.body,
      is_active: template.is_active,
      tenant_customizable: template.tenant_customizable || false,
      attachment_file_id: template.attachment_file_id || null,
      attachment_filename: template.attachment_filename || null,
    });
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingTemplate(null);
    setFormData({
      template_type: '',
      subject: '',
      body: '',
      is_active: true,
      tenant_customizable: false,
      attachment_file_id: null,
      attachment_filename: null,
    });
    setDialogOpen(true);
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachmentUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'email_template_attachment');
      const res = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData(prev => ({
        ...prev,
        attachment_file_id: res.data.id,
        attachment_filename: file.name,
      }));
      toast.success('Attachment uploaded');
    } catch {
      toast.error('Failed to upload attachment');
    } finally {
      setAttachmentUploading(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = () => {
    setFormData(prev => ({ ...prev, attachment_file_id: null, attachment_filename: null }));
    if (attachmentInputRef.current) attachmentInputRef.current.value = '';
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Email Templates</CardTitle>
          <Button onClick={handleAdd}>
            <Plus className="w-4 h-4 mr-2" /> New Template
          </Button>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Mail className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>No email templates yet</p>
              <Button onClick={handleAdd} variant="outline" className="mt-4">
                <Plus className="w-4 h-4 mr-2" /> Create First Template
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map(template => (
                <div key={template.id} className="flex items-center justify-between p-4 border rounded hover:bg-muted/30">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium">{template.name || EMAIL_TEMPLATE_TYPES.find(t => t.value === template.template_type)?.label || template.template_type}</p>
                      {template.is_active ? (
                        <Badge className="text-xs">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Inactive</Badge>
                      )}
                      {template.tenant_customizable && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">Tenant Customizable</Badge>
                      )}
                      {template.attachment_file_id && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground" title={template.attachment_filename || 'Has attachment'}>
                          <Paperclip className="w-3 h-3" />{template.attachment_filename || 'Attachment'}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{template.subject}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(template)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template Editor Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit' : 'Create'} Email Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template Type</Label>
              <Select value={formData.template_type} onValueChange={(value) => setFormData({ ...formData, template_type: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {EMAIL_TEMPLATE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Subject Line</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Email subject..."
              />
            </div>

            <div className="space-y-2">
              <Label>Email Body</Label>
              <RichTextEditor
                value={formData.body}
                onChange={(value) => setFormData({ ...formData, body: value })}
                placeholder="Email content..."
              />
              <p className="text-xs text-muted-foreground mt-2">
                See Template Variables card below for all available variables.
              </p>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Active</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.tenant_customizable}
                  onCheckedChange={(checked) => setFormData({ ...formData, tenant_customizable: checked })}
                />
                <Label>Tenant Customizable</Label>
              </div>
            </div>

            {/* Attachment */}
            <div className="space-y-2">
              <Label>Attachment</Label>
              {formData.attachment_file_id ? (
                <div className="flex items-center gap-2 p-2.5 border rounded bg-muted/30">
                  <Paperclip className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-sm flex-1 truncate">{formData.attachment_filename || 'Attached file'}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-red-600 shrink-0"
                    onClick={handleRemoveAttachment}
                    title="Remove attachment"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <div>
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleAttachmentUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => attachmentInputRef.current?.click()}
                    disabled={attachmentUploading}
                  >
                    {attachmentUploading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Paperclip className="w-3.5 h-3.5" />
                    )}
                    {attachmentUploading ? 'Uploading…' : 'Add Attachment'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">
                    This file will be attached to every email sent using this template.
                  </p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            {editingTemplate && !confirmingDelete && (
              <Button variant="ghost" size="icon" className="mr-auto text-muted-foreground hover:text-red-600" onClick={() => setConfirmingDelete(true)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            {editingTemplate && confirmingDelete && (
              <div className="flex items-center gap-2 mr-auto">
                <span className="text-sm text-red-600 font-medium">Delete this template?</span>
                <Button size="sm" variant="destructive" onClick={async () => {
                  try {
                    await api.delete(`/email-templates/${editingTemplate.id}`);
                    toast.success('Template deleted');
                    setConfirmingDelete(false);
                    setDialogOpen(false);
                    fetchTemplates();
                  } catch { toast.error('Failed to delete'); }
                }}>
                  Yes, Delete
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmingDelete(false)}>
                  Cancel
                </Button>
              </div>
            )}
            <Button variant="outline" onClick={() => { setConfirmingDelete(false); setDialogOpen(false); }}>Cancel</Button>
            <Button onClick={handleSave}>Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Variables Card — includes Custom section with management */}
      <TemplateVariablesCard
        collapsible
        defaultOpen={false}
        customVariables={customVariables}
        customManagement={(
          <div className="space-y-3 mt-1">
            {/* Existing custom variable remove buttons */}
            {customVariables.length > 0 && (
              <div className="space-y-1">
                {customVariables.map(v => (
                  <div key={v.key} className="flex items-center justify-between gap-2 px-2 py-1.5 border rounded text-xs bg-muted/20">
                    <div className="flex items-center gap-2 min-w-0">
                      <code className="text-primary font-medium shrink-0">{`{${v.key}}`}</code>
                      <span className="text-muted-foreground truncate">{v.label}</span>
                      {v.field_path && <span className="text-blue-600 text-[10px] bg-blue-50 border border-blue-200 px-1 py-0.5 rounded truncate">→ {v.field_path}</span>}
                      {v.static_value && !v.field_path && <span className="text-green-700 text-[10px] bg-green-50 border border-green-200 px-1 py-0.5 rounded">= "{v.static_value}"</span>}
                    </div>
                    <button className="text-red-400 hover:text-red-600 shrink-0 text-base leading-none" onClick={() => removeCustomVariable(v.key)}>×</button>
                  </div>
                ))}
              </div>
            )}

            {/* Add new variable form */}
            <div className="border rounded p-3 space-y-3 bg-muted/10">
              <p className="text-xs font-medium text-muted-foreground">Add New Variable</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Variable Key *</p>
                  <Input placeholder="sale_company" value={newVarKey} onChange={e => setNewVarKey(e.target.value)} className="h-7 text-xs" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Label *</p>
                  <Input placeholder="Sale Company" value={newVarLabel} onChange={e => setNewVarLabel(e.target.value)} className="h-7 text-xs" />
                </div>
              </div>

              <div className="flex gap-2">
                {['field','static'].map(mode => (
                  <button key={mode}
                    className={`text-xs px-2.5 py-1 rounded border transition-colors ${newVarMode === mode ? 'bg-primary text-white border-primary' : 'border-muted-foreground/30 text-muted-foreground hover:bg-muted/30'}`}
                    onClick={() => setNewVarMode(mode)}
                  >
                    {mode === 'field' ? 'Dynamic (DB Field)' : 'Static Value'}
                  </button>
                ))}
              </div>

              {newVarMode === 'field' ? (
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Data Source</p>
                  <div className="border rounded bg-white">
                    <div className="relative p-1 border-b">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                      <Input className="pl-6 h-6 text-xs border-0 focus-visible:ring-0 bg-transparent" placeholder="Search fields…" value={catalogSearch} onChange={e => setCatalogSearch(e.target.value)} />
                    </div>
                    <div className="max-h-44 overflow-y-auto">
                      {Object.entries(catalog).map(([group, fields]) => {
                        const filtered = fields.filter(f => !catalogSearch.trim() || f.label.toLowerCase().includes(catalogSearch.toLowerCase()) || f.path.toLowerCase().includes(catalogSearch.toLowerCase()));
                        if (!filtered.length) return null;
                        return (
                          <div key={group}>
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide px-2 pt-1.5 pb-0.5 sticky top-0 bg-white">{group}</p>
                            {filtered.map(f => (
                              <button key={f.path} type="button"
                                className={`w-full text-left px-2.5 py-1 text-xs hover:bg-muted/40 flex items-center justify-between gap-2 ${newVarFieldPath === f.path ? 'bg-primary/10 text-primary' : ''}`}
                                onClick={() => setNewVarFieldPath(f.path)}
                              >
                                <span className="font-medium">{f.label}</span>
                                <span className="text-muted-foreground text-[10px] font-mono truncate">{f.path}{f.example ? ` · "${f.example}"` : ''}</span>
                              </button>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                    {newVarFieldPath && (
                      <div className="border-t px-2.5 py-1 text-[10px] text-primary bg-primary/5 font-medium flex items-center justify-between">
                        <span>Selected: <code>{newVarFieldPath}</code></span>
                        <button className="text-muted-foreground hover:text-red-500" onClick={() => { setNewVarFieldPath(''); setCatalogSearch(''); }}>Clear</button>
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">Only populates in emails that have the matching context.</p>
                </div>
              ) : (
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Static Value — same in every email</p>
                  <Input placeholder="e.g. https://calendly.com/my-link" value={newVarStaticValue} onChange={e => setNewVarStaticValue(e.target.value)} className="h-7 text-xs" />
                </div>
              )}

              <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-white" onClick={addCustomVariable}
                disabled={!newVarKey.trim() || !newVarLabel.trim() || (newVarMode === 'field' && !newVarFieldPath) || (newVarMode === 'static' && !newVarStaticValue.trim())}
              >
                <Plus className="w-3 h-3 mr-1" /> Add Variable
              </Button>
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default EmailTemplatesTab;
